import { Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, FormGroup, Grid, Link as MuiLink, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AppConfig } from 'AppConfig';
import bstLogo from 'assets/img/BenInsights-Blue.png';
import axios from 'axios';
import { fetchData } from 'features/user/userActions';
import moment from 'moment-timezone';
import React, { useEffect, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import routePaths from 'routes/routePaths';
import * as AppActionTypes from 'store/actions/appstate';
import { store } from 'store/store';
import { MAX_QUESTIONNAIRE_COMMENT_LENGTH } from '../../../common/Constant';
import CarrierChangeDetails from './carrierChangeDetails';
import ContactInfo from './contactInfo';
import YearlyPocSection from './yearlyPocSection';

const CARRIER_FEEDS = [
  { stateKey: 'carrierMedical', prefix: 'medical', label: 'Medical' },
  { stateKey: 'carrierPharmacy', prefix: 'pharmacy', label: 'Pharmacy' },
  { stateKey: 'carrierEligibility', prefix: 'eligibility', label: 'Eligibility' },
  { stateKey: 'carrierAdmin', prefix: 'benAdmin', label: 'BenAdmin' },
  { stateKey: 'carrierBiometrics', prefix: 'biometrics', label: 'Biometrics' },
  { stateKey: 'carrierWellness', prefix: 'wellness', label: 'Wellness' },
  { stateKey: 'carrierOther', prefix: 'other', label: 'Other' }
];

const useStyles = makeStyles((theme) => ({
  page: {
    width: '100%',
    maxWidth: 1440,
    margin: '0 auto',
    padding: theme.spacing(2, 3, 4),
    color: '#17233f',
    fontFamily: 'Rubik, sans-serif',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1.5, 3)
    }
  },
  root: {
    display: 'flex'
  },
  bigFont: { fontSize: '1.5rem !important' },
  formControl: {
    marginBottom: theme.spacing(-1)
  },
  imgContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2, 0)
  },
  logo: {
    maxWidth: '100%',
    maxHeight: 80,
    height: 'auto',
    width: 'auto',
    objectFit: 'contain',
    display: 'block'
  },
  inputField: {
    margin: theme.spacing(1),
    '& .MuiInputBase-root': {
      height: '25px'
    },
    width: '150px'
  },
  label: {
    fontWeight: '500',
    marginRight: theme.spacing(1),
    alignSelf: 'center'
  },
  sectionTitle: {
    color: '#004c97',
    fontWeight: 700
  },
  questionBlock: {
    marginBottom: theme.spacing(2)
  },
  detailPanels: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  leftFill: { marginLeft: '25px' },
  smallFont: { fontSize: '0.8rem !important' },
  gridItem: { display: 'flex', justifyContent: 'space-around' }
}));

const ClientPortalQuestionary = (props) => {
  const location = useLocation();
  const methods = useForm();
  const { control, formState, getValues } = methods;
  const navState = location.state || {};
  const companies = useSelector((state) => state.user.companies);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const defaultUserState = useSelector((state) => state.user.defaultUserState);
  const isUpdating = useSelector((state) => state.user.isUpdating);

  // A browser refresh resets the store, so the tenant can only be judged once user data has loaded.
  const isUserHydrated = !defaultUserState && !isUpdating;
  const questionnaireTenantCd = navState?.tenantCode || currentTenantCd;
  const currentTenant = Array.isArray(companies) ? companies.filter((c) => c.code === questionnaireTenantCd) : [];
  const [state, setState] = React.useState({
    caseId: navState?.caseId,
    newPlanYearStart: navState?.newPlanStartYear ? moment(navState?.newPlanStartYear, 'MM/DD/YYYY').format('MM-YYYY') : moment().startOf('year').add(1, 'years').format('MM-YYYY'),
    tenantCode: navState?.tenantCode,
    tenantName: navState?.tenantName,
    brokerName: navState?.brokerName,
    renewalAccountType: navState?.renewalAccountType,
    comments: '',
    planFunding: '',
    carrierChange: false,
    billingChange: false,

    carrierMedical: false,
    carrierPharmacy: false,
    carrierEligibility: false,
    carrierAdmin: false,
    carrierBiometrics: false,
    carrierWellness: false,
    carrierOther: false
  });

  const styles = useStyles();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [bootstrapTimedOut, setBootstrapTimedOut] = React.useState(false);
  const [bootstrapAttempt, setBootstrapAttempt] = React.useState(0);
  const CLIENT_PORTAL_DASHBOARD_URL = routePaths.clientPortal;
  const ANNUAL_PLAN_RENEWAL_URL = CLIENT_PORTAL_DASHBOARD_URL + AppConfig.CLIENT_PORTAL_SUB_URL.ANNUAL_PLAN_RENEWAL;
  const hasSelectedCarrierFeed = CARRIER_FEEDS.some(({ stateKey }) => state[stateKey]);

  const componentRef = useRef();
  // handleSubmit validates asynchronously, so overlapping clicks need a lock that updates synchronously.
  const submitLockRef = useRef(false);

  const onSubmit = () => {
    if (submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;
    setIsSubmitting(true);

    const values = getValues();

    const myData = {
      ...state,
      ...values,
      submitTime: moment(new Date()).format('MM/DD/YYYY HH:mm UTC')
    };
    axios.post(AppConfig.CLIENT_PORTAL_URL + '/case-management/questionnaire', myData).then(
      (response) => {
        navigate(ANNUAL_PLAN_RENEWAL_URL);
      },
      (error) => {
        console.error(error);
        submitLockRef.current = false;
        setIsSubmitting(false);
      }
    );
  };

  const isSubmitEnabled = () => {
    const { carrierChangeYN, billingChangeYN, planFundingValue } = getValues();

    if (!carrierChangeYN || !billingChangeYN || !state.planFunding) {
      return false;
    }

    if (state.carrierChange && !CARRIER_FEEDS.some(({ stateKey }) => state[stateKey])) {
      return false;
    }

    if (!['Self Insured', 'Fully Insured', 'Level Funded'].includes(planFundingValue)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    store.dispatch({
      type: AppActionTypes.SHOW_BST_BAR_TENANT,
      payload: false
    });
    return () => {
      store.dispatch({
        type: AppActionTypes.SHOW_BST_BAR_TENANT,
        payload: true
      });
    };
  }, []);

  // This route renders outside the client portal navigator, so it has to bootstrap the user itself.
  useEffect(() => {
    const bootstrap = setTimeout(() => {
      if (defaultUserState) {
        store.dispatch(fetchData(currentTenantCd, AppConfig.APP_CODE_CLIENT_PORTAL, { explicitTenant: false }));
      }
    }, 100);
    // A stalled bootstrap would otherwise leave the loader up forever.
    const deadline = setTimeout(() => setBootstrapTimedOut(true), 15000);
    return () => {
      clearTimeout(bootstrap);
      clearTimeout(deadline);
    };
  }, [bootstrapAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetryBootstrap = () => {
    setBootstrapTimedOut(false);
    setBootstrapAttempt((attempt) => attempt + 1);
  };

  // Without a case id the submission cannot be tied to a renewal case, so never offer the form.
  if (!state.caseId) {
    return (
      <div className={styles.page}>
        <Typography className={styles.sectionTitle} sx={{ mb: 1 }}>
          We could not determine which renewal case this questionnaire belongs to.
        </Typography>
        <Typography>
          Please reopen the questionnaire from{' '}
          <MuiLink component={RouterLink} to={ANNUAL_PLAN_RENEWAL_URL} underline="always">
            Annual Plan Renewal
          </MuiLink>
          .
        </Typography>
      </div>
    );
  }

  if (!isUserHydrated && bootstrapTimedOut) {
    return (
      <div className={styles.page}>
        <Typography className={styles.sectionTitle} sx={{ mb: 1 }}>
          We could not load your questionnaire.
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Please try again, or reopen the questionnaire from{' '}
          <MuiLink component={RouterLink} to={ANNUAL_PLAN_RENEWAL_URL} underline="always">
            Annual Plan Renewal
          </MuiLink>
          .
        </Typography>
        <Button variant="contained" onClick={handleRetryBootstrap}>
          Try again
        </Button>
      </div>
    );
  }

  if (!isUserHydrated) {
    return (
      <div className={styles.page}>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress aria-label="Loading questionnaire" />
        </Box>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div ref={componentRef} className={styles.page}>
        <>
          {currentTenant && currentTenant[0] ? (
            <>
              <div className={styles.imgContainer}>
                <Box component="img" src={bstLogo} alt="BenInsights logo" className={styles.logo} />
              </div>
              <div align="center">
                <br />
                <Typography variant="h6" color="primary">
                  {state.tenantName}
                </Typography>
              </div>
              <div align="center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <label htmlFor="planStartYear" className={styles.label}>
                  Plan Year Start: {state.newPlanYearStart}
                </label>
              </div>
              <br />
              <Divider color="primary" />
              <Typography className={styles.sectionTitle} sx={{ mt: 2 }}>
                <strong style={{ fontSize: '1.15em' }}>Plan Funding Type</strong>
                <br />
              </Typography>
              <Grid item xs={6}>
                <FormControl component="fieldset">
                  <Controller
                    name="planFundingValue"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState }) => (
                      <RadioGroup
                        row
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setState({ ...state, planFunding: e.target.value });
                        }}>
                        <FormControlLabel value="Self Insured" control={<Radio style={{ color: 'blue' }} />} label="Self Insured" />
                        <FormControlLabel
                          value="Fully Insured"
                          control={<Radio style={{ color: 'blue' }} />}
                          label="Fully Insured"
                        />
                        <FormControlLabel value="Level Funded" control={<Radio style={{ color: 'blue' }} />} label="Level Funded" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
              <Typography color="primary">
                <strong style={{ fontSize: '1.15em' }}>Are there any carrier changes or additions from what is being loaded to BenInsights, in the coming plan year?</strong>
                <br />
                <span className={styles.smallFont}>(Excluding Stop Loss)</span>
              </Typography>
              <Grid item xs={6}>
                <FormControl component="fieldset">
                  <Controller
                    name="carrierChangeYN"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setState({ ...state, carrierChange: e.target.value === 'yes' });
                        }}>
                        <FormControlLabel value="yes" control={<Radio style={{ color: 'blue' }} />} label="Yes" />
                        <FormControlLabel
                          value="no"
                          control={
                            <Radio
                              style={{ color: 'blue' }}
                              onClick={() => {
                                setState({
                                  ...state,
                                  carrierChange: false,
                                  carrierMedical: false,
                                  carrierPharmacy: false,
                                  carrierEligibility: false,
                                  carrierAdmin: false,
                                  carrierBiometrics: false,
                                  carrierWellness: false,
                                  carrierOther: false
                                });
                              }}
                            />
                          }
                          label="No"
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          ) : (
            <p>Invalid Tenant</p>
          )}

          {state.carrierChange && (
            <>
              <div>
                <Typography color="primary" style={{ display: 'inline-block' }}>
                  <strong style={{ fontSize: '1.15em' }}>Changing or adding data feeds:</strong>
                  <br />
                  <span className={styles.smallFont}>
                    (Please identify any change or additional data that you would like to include and click all that apply. These changes could incur a fee as outlined in the
                    estimate contract.)
                  </span>
                </Typography>
              </div>
              <div>
                <FormControl required component="fieldset" className={styles.formControl}>
                  <FormGroup row>
                    {CARRIER_FEEDS.map(({ stateKey, prefix, label }) => (
                      <FormControlLabel
                        key={stateKey}
                        control={
                          <Checkbox
                            checked={state[stateKey]}
                            onChange={() => setState({ ...state, [stateKey]: !state[stateKey] })}
                            name={prefix}
                          />
                        }
                        label={label}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </div>
              {hasSelectedCarrierFeed && (
                <div className={styles.detailPanels}>
                  {CARRIER_FEEDS.filter(({ stateKey }) => state[stateKey]).map(({ stateKey, prefix, label }) => (
                    <Box key={stateKey} sx={{ mb: 1.5 }}>
                      <CarrierChangeDetails prefix={prefix} label={label} />
                    </Box>
                  ))}
                </div>
              )}
            </>
          )}

          <YearlyPocSection />

          <Typography className={styles.sectionTitle}>
            <strong style={{ fontSize: '1.15em' }}>Billing Changes:</strong>
            <br />
            <span className={styles.smallFont}>(Has the billing contact or responsible party changed?)</span>
          </Typography>
          <Grid item xs={6}>
            <FormControl component="fieldset">
              <Controller
                name="billingChangeYN"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setState({ ...state, billingChange: e.target.value === 'yes' });
                    }}>
                    <FormControlLabel value="yes" control={<Radio style={{ color: 'blue' }} />} label="Yes" />
                    <FormControlLabel
                      value="no"
                      control={
                        <Radio
                          style={{ color: 'blue' }}
                          onClick={() => {
                            setState({ ...state, billingChange: false });
                          }}
                        />
                      }
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Grid>

          {state.billingChange && (
            <>
              <Grid container spacing={4}>
                <Grid item xs={4}>
                  <Grid container alignItems="center" className={styles.gridItem}>
                    <Grid item>
                      <label htmlFor={'billingEntityName'} className={styles.label}>
                        Legal Entity Name
                      </label>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="billingEntityName"
                        control={control}
                        defaultValue=""
                        rules={{ required: state.billingChange }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            margin="dense"
                            size="small"
                            className={styles.inputField}
                            variant="outlined"
                            required={state.billingChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error ? 'Legal Entity Name is required' : ''}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Grid container alignItems="center" className={styles.gridItem}>
                    <Grid item>
                      <label htmlFor={'billingAddress'} className={styles.label}>
                        Billing Address
                      </label>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="billingAddress"
                        control={control}
                        defaultValue=""
                        rules={{ required: state.billingChange }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            margin="dense"
                            size="small"
                            className={styles.inputField}
                            variant="outlined"
                            error={!!fieldState.error}
                            helperText={fieldState.error ? 'Billing Address is required' : ''}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <ContactInfo prefix="billing" />
            </>
          )}

          <Typography color="primary">
            <strong style={{ fontSize: '1.15em' }}>Additional Comments</strong>
            <span className={styles.smallFont}>&nbsp;- optional</span>
          </Typography>
          <Controller
            name="comments"
            control={control}
            defaultValue=""
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                margin="dense"
                size="small"
                variant="outlined"
                multiline
                minRows={4}
                onChange={(e) => {
                  field.onChange(e);
                  setState({ ...state, comments: e.target.value });
                }}
                error={!!fieldState.error}
                helperText={fieldState.error ? `Maximum length is ${MAX_QUESTIONNAIRE_COMMENT_LENGTH} characters` : ''}
              />
            )}
            rules={{ required: false, maxLength: MAX_QUESTIONNAIRE_COMMENT_LENGTH }}
          />
          <p align="center">
            Contact{' '}
            <a href="mailto: insights-renewals@multiplan.com" style={{ textDecoration: 'underline' }}>
              Insights-Renewals
            </a>{' '}
            with any questions.
          </p>
          <br />
          <Button
            key={Object.values(state).join('-') + '-' + formState.dirtyFields.billingChangeYN}
            style={{
              backgroundColor: isSubmitEnabled() && !isSubmitting ? '#377DC7' : '#B0C4DE',
              height: '40px',
              width: '140px',
              marginTop: '5px',
              color: 'white',
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',
              fontSize: '1.1em'
            }}
            onClick={methods.handleSubmit(onSubmit)}
            disabled={!isSubmitEnabled() || isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </>
      </div>
    </FormProvider>
  );
};

export default ClientPortalQuestionary;
